import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { BudgetType, StatusType } from "../../../common/types/roots/fields";
import i18nService from "@/services/i18n";
import { report127DailyExpenses, report127DailyFilterOptions, report127DailyIndicators } from "../report-127-daily";
import { Report127DailyFilterSettingsForm } from "../settings-form/Report127DailyFilterSettingsForm";
import { Report127DailyFilterSettings } from "../settings/Report127DailyFilterSettings";
import { Report127DailyTableDataRequest } from "../other-types/Report127DailyTableDataRequest";
import { allExpenseTypesOptions, allIncomeClassificators, allSegmentationOptions, allStatusOptions } from "../../../common/baseOptions";
import { Report127DailyJournalRequest } from "../journal/Report127DailyJournalRequest";
import { Report127DailyRow, Report127DailyTableRow, SectionSeparator } from "../table-data/table-data-types";

export class Report127DailyFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(report127DailyFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== '1-27-DAILY') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== '1-27-DAILY') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        // Дата
        let date: Date | null = null
        if (settings.date) {
            date = moment(settings.date, 'YYYY-MM-DD').toDate()
        }

        // Разрезность (segmentations)
        const segmentations = cloneDeep(settings.segmentations)

        // Статусы
        const statuses: Option<StatusType>[] = options.statuses.filter(it => settings.statuses.includes(it.value))

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }
        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))

        let measureUnit = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnit) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnit = { ...measureUnit }

        let budgetStructure = options.budgetStructures.find(item => item.value == settings.budgetStructure) ?? null
        if (budgetStructure) {
            budgetStructure = {...budgetStructure}
        }

        let developType = options.developTypes.find(it => it.value === settings.developType)
        if (!developType) {
            throw new Error('error_not_found_develop_type')
        }
        developType = { ...developType }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        let byForm = options.byForms.find(it => it.value === settings.byForm?.value) ?? options.byForms[0]
        if (!byForm) {
            throw new Error('error_not_found_by_form')
        }
        byForm = { ...byForm }

        let expenseType = options.expenseTypes.find(it => it.value === settings.expenseType) ?? null
        if (!expenseType) {
            throw new Error('error_not_found_expense_type')
        }
        expenseType = { ...expenseType }

        // 2. Формирование
        const form: Report127DailyFilterSettingsForm = {
            key: '1-27-DAILY',

            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,

            date: date,
            regions: regions,
            segmentations: segmentations,
            statuses: statuses,
            byForm: byForm,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            budgetTypes: budgetTypes,
            measureUnit: measureUnit,
            budgetStructure: budgetStructure,
            developType: developType,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,
            incomeTotal: settings.incomeTotal,
            expenseTotal: settings.expenseTotal,
            msuAggregation: settings.msuAggregation ?? false,

            expenseType: expenseType,
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators),
            isTree: settings.isTree
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== '1-27-DAILY') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.budgetStructure == null) {
            throw new Error('error_input_budget_struct')
        }
        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }
        if (form.date == null) {
            throw new Error('error_input_date')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        // Конвертация даты
        const dateStr = moment(form.date).format('YYYY-MM-DD')

        // Статусы
        const statuses = form.statuses.map(it => it.value)

        const settings: Report127DailyFilterSettings = {
            reportName: '1-27-DAILY',

            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,

            date: dateStr,
            regions: form.regions.map(it => it.value),
            segmentations: cloneDeep(form.segmentations),
            statuses: statuses,
            byForm: form.byForm ?? { value: '5-42', label: '2-19 и 5-42' },
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            budgetTypes: form.budgetTypes.map(it => it.value),
            measureUnit: form.measureUnit.value,
            budgetStructure: form.budgetStructure.value,
            developType: form.developType.value ?? null,
            roundUpTo: roundUpTo,
            incomeTotal: form.incomeTotal,
            expenseTotal: form.expenseTotal,
            msuAggregation: form.msuAggregation ?? false,
            expenseType: form.expenseType.value,
            incomes: cloneDeep(form.incomes),
            expenses: cloneDeep(form.expenses),
            indicators: cloneDeep(form.indicators),
            isTree: form.isTree
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(report127DailyFilterOptions)

        // Разрезность по умолчанию: только КБК активно
        const defaultSegmentations = cloneDeep(allSegmentationOptions).map(s => ({
            ...s,
            active: s.value === 'KBK'
        }))

        // Статус по умолчанию: 39 - Утверждено в ИС КК
        const defaultStatuses = cloneDeep(allStatusOptions).filter(s => s.value === '39')

        const emptyForm: Report127DailyFilterSettingsForm = {
            key: '1-27-DAILY',

            reportName: options.reportNames.find(it => it.value === '1-27-DAILY')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,

            date: null,
            regions: [],
            segmentations: defaultSegmentations,
            statuses: defaultStatuses,
            byForm: options.byForms.find(it => it.value === '5-42')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITH')!,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            budgetTypes: [],
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            budgetStructure: null,
            developType: options.developTypes.find(it => it.value === null)!,
            roundUp: false,
            roundUpTo: null,
            incomeTotal: false,
            expenseTotal: false,
            msuAggregation: false,

            expenseType: options.expenseTypes.find(it => it.value === 'FUNCTIONAL')!,
            incomes: cloneDeep(allIncomeClassificators),
            expenses: cloneDeep(report127DailyExpenses),
            indicators: cloneDeep(report127DailyIndicators),
            isTree: true
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций
        if (form.key !== '1-27-DAILY') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if ('regions' in form && form.regions.length === 0) {
            throw new Error('error_input_region')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }
        const developType = form.developType.value


        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        const mutually_redeeming_levels = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })

        // Конвертация даты
        const dateStr = moment(form.date).format('YYYY-MM-DD')

        // Извлечение года и месяца из даты
        const year = moment(form.date).year()
        const month = moment(form.date).month() + 1 // moment месяцы начинаются с 0

        // Статусы
        const statuses = form.statuses.map(it => it.value)

        // Разрезность
        const segmentations = form.segmentations.filter(s => s.active).map(s => s.value)

        // 2. Формирование данных для таблицы
        const tempIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        const tempExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        // by_form используется только при источнике данных "Загрузчик"
        const byFormValue = form.dataSource.value === 'LOADER' ? (form.byForm?.value ?? '5-42') : null

        const params: Report127DailyTableDataRequest = {
            incomes: tempIncomes,
            expenses: tempExpenses,
            expense_type: form.expenseType.value,
            by_form: byFormValue,
            year: year,
            month: month,
            date: dateStr,
            regions: form.regions.map(it => it.value),
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: mutually_redeeming_levels,
            budget_structure: form.budgetStructure?.value ?? null,
            develop_type: developType,
            income_total: form.incomeTotal,
            expense_total: form.expenseTotal,
            statuses: statuses,
            segmentations: segmentations,
            is_tree: form.isTree,
            data_source: form.dataSource.value,
            msu_aggregation: form.msuAggregation ?? false
        }
        const response = await fetch('/api-py/monitoring/reports-constructor/1-27-daily',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData = await response.json()

        let index = 0
        // functions are defined in this scope to use 'index' variable for unique keys
        const mapRow = (item: any): Report127DailyTableRow => {
            const mapped: Report127DailyTableRow = {
                index: index++,

                gr: item.gr ?? null,
                pgr: item.pgr ?? null,
                abp: item.abp ?? null,
                prg: item.prg ?? null,
                ppr: item.ppr ?? null,
                epcl: item.epcl ?? null,
                espf: item.espf ?? null,

                gu: item.gu != null ? String(item.gu) : null,

                kat: item.kat ?? null,
                cls: item.cls ?? null,
                pcl: item.pcl ?? null,
                spf: item.spf ?? null,

                nameRu: item.name ?? null,
                nameKk: item.name_kk ?? null,

                utv: roundToDecimal(item.utv ?? null, roundUpTo),
                utch: roundToDecimal(item.utch ?? null, roundUpTo),
                plg: roundToDecimal(item.plg ?? null, roundUpTo),
                plgp: roundToDecimal(item.plgp ?? null, roundUpTo),
                plgo: roundToDecimal(item.plgo ?? null, roundUpTo),
                obz: roundToDecimal(item.obz ?? null, roundUpTo),
                nobz: roundToDecimal(item.nobz ?? null, roundUpTo),
                sumrg: roundToDecimal(item.sumrg ?? null, roundUpTo),
                percentage1: item.percentage1 ?? null,
                percentage2: item.percentage2 ?? null,

                bold: item.bold
            }
            return mapped
        }

        const tempTableData: Report127DailyRow[] = []

        responseTableData.forEach((it: any) => {
            if (it.separator === 'REGION') {
                const separator: SectionSeparator = { name: it.name, type: 'REGION' }
                tempTableData.push(separator)
                return
            }
            if (it.utv !== undefined) {
                const mapped = mapRow(it)
                tempTableData.push(mapped)
            }
        })

        // 3. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: Report127DailyJournalRequest = {
            reportName: "1-27-DAILY",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            incomes_order: form.incomes.filter(it => it.active).map(it => it.key),
            expenses_order: (() => {
                const order = form.expenses.filter(it => it.active).map(it => it.key)
                if (segmentations.includes('GU') && !order.includes('gu')) {
                    order.push('gu')
                }
                return order
            })(),
            expense_type: form.expenseType.value,
            income_total: form.incomeTotal,
            expense_total: form.expenseTotal,
            indicators_order: form.indicators.filter(it => it.active).map(it => it.key),
            table_data: [],
            data_request: params,
            date: dateStr,
            region_labels: form.regions.map(it => it.label),
            mutually_redeeming: form.mutuallyRedeeming.value,
            separate_by_region: false,
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit_label: form.measureUnit.label,
            measure_unit: form.measureUnit.value,
            round_up: roundUpTo,
            locale: i18nService.locale,
            excel_version: 'TC', // hard-coded here but is meant to be set dynamically when manually chosen in download options
        }

        const result: TableDataAndJournalParams = {
            tableData: tempTableData,
            journalParams: journalParams
        }

        return result
    }
}

const roundToDecimal = (value: number | null, scale: number | null): number | null => {
    if (scale == null || value == null) {
        return value
    }
    const factor = Math.pow(10, scale)
    return Math.round((value + Number.EPSILON) * factor) / factor
}
